import React, { useMemo } from "react";
import { PROJECT_SKINS, SKIN_GROUPS, skinToConfig, DEFAULT_THEME_APPLY } from "../../lib/gamecraft";
import { BG_PRESETS, themeBackgroundUrl, pixelateBackgroundFile } from "../../lib/themeBackgrounds";
import { toast } from "sonner";

const APPLY_LABELS = [
  { id: "background", label: "Fondo" },
  { id: "containers", label: "Contenedores" },
  { id: "highlights", label: "Resaltados" },
  { id: "text", label: "Letras" },
  { id: "buttons", label: "Botones" },
  { id: "banners", label: "Banners" },
];

export default function SkinPicker({
  value,
  onSelect,
  applyTargets,
  onApplyChange,
  bgPreset,
  bgImage,
  bgUploads = [],
  bgSource,
  onBgChange,
}) {
  const current = value || "aurora";
  const apply = { ...DEFAULT_THEME_APPLY, ...(applyTargets || {}) };
  const thumbs = useMemo(
    () => Object.fromEntries(BG_PRESETS.map((b) => [b.id, themeBackgroundUrl(b.id)])),
    []
  );
  const activePreset = bgSource === "none" ? "" : (bgImage ? "" : (bgPreset || current));

  const pickPreset = (id) => {
    onBgChange?.({
      bg_preset: id,
      bg_source: "preset",
      bg_image: "",
    });
  };

  const uploadBg = async (file) => {
    try {
      const data_url = await pixelateBackgroundFile(file);
      const item = { id: crypto.randomUUID(), name: file.name.replace(/\.[^.]+$/, "").slice(0, 32), data_url };
      const next = [...(bgUploads || []).slice(-7), item];
      onBgChange?.({
        bg_uploads: next,
        bg_image: data_url,
        bg_source: "custom",
        bg_preset: "",
      });
      toast.success("Fondo pixelado e importado");
    } catch (e) {
      toast.error(e.message || "No se pudo usar esa imagen");
    }
  };

  return (
    <div className="space-y-4">
      {onApplyChange && (
        <div>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--gc-muted)" }}>Aplicar tema a</div>
          <div className="flex flex-wrap gap-1.5">
            {APPLY_LABELS.map((a) => (
              <button
                key={a.id}
                type="button"
                className={"gc-chip " + (apply[a.id] !== false ? "gc-chip-on" : "")}
                onClick={() => onApplyChange({ ...apply, [a.id]: apply[a.id] === false })}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {SKIN_GROUPS.map((group) => (
        <div key={group}>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--gc-muted)" }}>{group}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {PROJECT_SKINS.filter((s) => s.group === group).map((s) => {
              const on = current === s.id;
              const preview = thumbs[s.id] || thumbs.aurora;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onSelect(skinToConfig(s))}
                  className="rounded-2xl overflow-hidden text-left border transition-transform hover:scale-[1.02]"
                  style={{
                    borderColor: on ? "#fff" : "var(--gc-border)",
                    boxShadow: on ? `0 0 0 2px ${s.primary}` : undefined,
                  }}
                >
                  <div className="h-16 relative overflow-hidden" style={{ background: s.banner }}>
                    {preview && (
                      <img
                        src={preview}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ imageRendering: "pixelated" }}
                      />
                    )}
                    <div className="absolute bottom-1.5 left-2 right-2 flex gap-1">
                      <span className="w-3 h-3 rounded-full border border-white/40" style={{ background: s.primary }} />
                      <span className="w-3 h-3 rounded-full border border-white/40" style={{ background: s.accent2 }} />
                    </div>
                  </div>
                  <div className="px-2.5 py-2" style={{ background: "var(--gc-surface-strong)" }}>
                    <div className="font-semibold text-sm">{s.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--gc-muted)" }}>{s.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {onBgChange && (
        <div>
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--gc-muted)" }}>Fondos pixel art</div>
          <p className="text-xs mb-2" style={{ color: "var(--gc-muted)" }}>
            Repertorio 16-bit generado. Elige uno o sube el tuyo (se pixeliza a 160×90).
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              className={"gc-chip " + (bgSource === "none" ? "gc-chip-on" : "")}
              onClick={() => onBgChange({ bg_source: "none", bg_image: "", bg_preset: "" })}
            >
              Sin imagen
            </button>
            <label className="gc-chip cursor-pointer">
              Subir fondo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) uploadBg(f);
                }}
              />
            </label>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {BG_PRESETS.map((b) => {
              const on = activePreset === b.id && bgSource !== "custom";
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => pickPreset(b.id)}
                  className="rounded-xl overflow-hidden border text-left"
                  style={{
                    borderColor: on ? "var(--gc-primary)" : "var(--gc-border)",
                    boxShadow: on ? "0 0 0 2px var(--gc-primary)" : undefined,
                  }}
                  title={b.name}
                >
                  <img src={thumbs[b.id]} alt={b.name} className="w-full h-12 object-cover" style={{ imageRendering: "pixelated" }} />
                  <div className="px-1.5 py-1 text-[10px] truncate">{b.name}</div>
                </button>
              );
            })}
          </div>
          {(bgUploads || []).length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--gc-muted)" }}>Tus fondos</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {bgUploads.map((u) => {
                  const on = bgSource === "custom" && bgImage === u.data_url;
                  return (
                    <div key={u.id} className="relative">
                      <button
                        type="button"
                        onClick={() => onBgChange({ bg_source: "custom", bg_image: u.data_url, bg_preset: "" })}
                        className="rounded-xl overflow-hidden border w-full text-left"
                        style={{ borderColor: on ? "var(--gc-primary)" : "var(--gc-border)" }}
                      >
                        <img src={u.data_url} alt={u.name} className="w-full h-12 object-cover" style={{ imageRendering: "pixelated" }} />
                        <div className="px-1.5 py-1 text-[10px] truncate">{u.name}</div>
                      </button>
                      <button
                        type="button"
                        className="absolute top-1 right-1 text-[10px] bg-black/60 text-white rounded px-1"
                        onClick={() => {
                          const next = bgUploads.filter((x) => x.id !== u.id);
                          const clearing = bgImage === u.data_url;
                          onBgChange({
                            bg_uploads: next,
                            ...(clearing ? { bg_image: "", bg_source: "preset", bg_preset: current } : {}),
                          });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
